import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Integration, Log, Task } from "/app/src/models";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import Controls from "./_controls";
import LogsTable from "/app/src/components/generic/components/tasks/logsTable";
import { createColumnHelper } from "@tanstack/react-table";
import TasksTable from "/app/src/components/generic/components/tasks/tasksTable";
import { Col, Row, InputNumber } from "antd";
import { taskService } from "/app/src/services";
import StatusIcon from "/app/src/components/generic/components/statusIcon";
import {
  buildParams,
  getJSON,
  getKeyValue,
  parseOutData,
} from "/app/src/helpers";
import RedButton from "/app/src/components/HeroUi/RedButton";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Button,
  Chip,
} from "@heroui/react";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";

/**
 * Component for displaying the logs of a data push integration
 * @param integration the integration to display the logs for
 */
export default function DataPullLogs({
  integration,
}: {
  integration: Integration;
}) {
  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [status, setStatus] = useState<string>("");
  const { t } = useTranslation();
  const [taskId, setTaskId] = useState<number | undefined>(undefined);
  const [logParams, setLogParams] = useState(undefined);
  const [taskParams, setTaskParams] = useState(undefined);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [numberToCancel, setNumberToCancel] = useState(0);

  const { data: tasksPending, refetch } = useQuery({
    queryKey: ["tasksPending", "count"],
    queryFn: () =>
      taskService.getCount(
        buildParams({
          integrationId: integration.id,
          status: 2,
          workflowId: null,
        }),
      ),
    initialData: { count: 0 },
    refetchInterval: 10000,
    select: (data: { count: number }) => data.count,
  });

  const handleRefetch = useCallback(() => {
    setRefetchTrigger((prev) => !prev);
    refetch();
  }, [refetch]);

  const renderPendingTasks = useCallback(() => {
    return (
      <Chip className="bg-secondary-accent" color="warning">
        {t("translation:queued_tasks")}: {tasksPending}
      </Chip>
    );
  }, [t, tasksPending]);

  const inputNumberOnChange = useCallback((value: number) => {
    setNumberToCancel(value);
  }, []);

  const onSubmit = useCallback(async () => {
    return await taskService
      .bulkDelete(
        new URLSearchParams({
          integrationId: integration.id.toString(),
          limit: numberToCancel.toString(),
          status: "2",
          orderby: "creationDate:asc",
          workflowId: "null",
        }),
      )
      .then((response) => {
        toast.success(`${response.deleted} ${t("translation:tasks_deleted")}`);
        onOpenChange();
        setNumberToCancel(0);
        handleRefetch();
      });
  }, [integration.id, numberToCancel, onOpenChange, handleRefetch, t]);

  useEffect(() => {
    if (searchString === "") {
      setTaskParams({
        integrationId: integration.id,
        status,
        workflowId: null,
      });
    } else {
      setTaskParams({
        integrationId: integration.id,
        search: `${searchString} OR Log.description=[like]${searchString}`,
        status,
        workflowId: null,
      });
    }
  }, [integration.id, searchString, status]);

  useEffect(() => {
    setLogParams({
      integrationId: integration.id,
      taskId,
    });
  }, [integration.id, taskId]);

  const columnHelper = createColumnHelper<Log>();
  const taskColumnHelper = createColumnHelper<Task>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => <DateTime date={info.getValue()} />,
        header: () => t("translation:creationDate"),
        meta: {
          clickable: false,
        },
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: () => t("translation:description"),
        meta: {
          clickable: false,
        },
      }),
      columnHelper.accessor("status", {
        id: "status",
        cell: (info) => {
          const value = info.getValue();
          return <StatusIcon status={value} />;
        },
        header: () => t("translation:status"),
        meta: {
          clickable: false,
        },
      }),
    ],
    [columnHelper, t],
  );

  const taskColumns = useMemo(
    () => [
      taskColumnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => <DateTime date={info.getValue()} />,
        header: t("translation:time"),
      }),
      taskColumnHelper.accessor("data", {
        id: "data",
        header: () => t("translation:data"),
        cell: (info) => (
          <p>{getJSON(parseOutData(info.getValue(), "record"))}</p>
        ),
      }),
      taskColumnHelper.accessor("data", {
        id: "test",
        header: () => t("translation:test"),
        cell: (info) =>
          getKeyValue(info.getValue(), "isTest") ? (
            <StatusIcon status={1} />
          ) : (
            <> </>
          ),
      }),
      taskColumnHelper.accessor("status", {
        id: "status",
        cell: (info) => {
          const value = info.getValue();
          return <StatusIcon status={value} />;
        },
        header: t("translation:status"),
      }),
    ],
    [taskColumnHelper, t],
  );

  const onModalOpen = useCallback(() => {
    onOpen();
    refetch();
  }, [onOpen, refetch]);

  const handleSetTaskId = useCallback(
    (id?: number) => {
      if (id === undefined) {
        setTaskId(undefined);
      } else {
        setTaskId((prev) => (prev === id ? undefined : id));
      }
    },
    [setTaskId],
  );

  const handleClick = useCallback(async () => {
    await taskService
      .updateSingle(taskId, { retry: "true" })
      .then((res: { retried: boolean }) => {
        if (res?.retried) {
          toast.success(t("translation:task_retried"));
        } else {
          toast.error(t("translation:task_retry_failed"));
          setButtonDisabled(true);
        }
      });
  }, [t, taskId]);

  return (
    <Row gutter={16}>
      <Col span={10}>
        <div className="flex justify-between items-center mb-4">
          {renderPendingTasks()}
          <RedButton
            onPress={onModalOpen}
            radius="none"
            isDisabled={tasksPending === 0}
          >
            {t("translation:cancel_tasks")}
          </RedButton>
          <Modal
            size="lg"
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            aria-label="Cancel tasks"
          >
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex items-center gap-2">
                    <IconBuilder icon="Warning" size={24} color="#FF5E2F" />
                    {t("translation:cancel_tasks")}
                  </ModalHeader>
                  <ModalBody>
                    <div className="flex flex-col">
                      <p className="text-[20px]">
                        {t("translation:tasks_modal_description")}
                      </p>
                      <InputNumber
                        min={0}
                        value={numberToCancel}
                        onChange={inputNumberOnChange}
                        size="large"
                      />
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="default"
                      radius="none"
                      variant="bordered"
                      onPress={onClose}
                    >
                      {t("translation:close")}
                    </Button>
                    <Button color="primary" radius="none" onPress={onSubmit}>
                      {t("translation:confirm")}
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>
        <Controls setSearchString={setSearchString} setStatus={setStatus} />
        <TasksTable
          integrationId={integration.id}
          emptyText={t("translation:no_tasks_found")}
          setTaskId={handleSetTaskId}
          params={taskParams}
          refresh
          columns={taskColumns}
          refetchQueries={refetchTrigger}
        />
      </Col>
      <Col span={14}>
        {taskId !== undefined && (
          <div className="flex flex-col">
            <div className="self-end mb-2">
              <Button
                color="primary"
                onPress={handleClick}
                isDisabled={buttonDisabled}
              >
                {t("translation:requeue_task")}
              </Button>
            </div>

            <LogsTable
              notFoundText={t("translation:no_logs_found")}
              columns={columns}
              params={logParams}
              refresh
            />
          </div>
        )}
      </Col>
    </Row>
  );
}
